import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  MinusCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useMyAppeals, useWithdrawAppeal, useAppeal } from '@/hooks/use-appeals';
import { cn } from '@/lib/utils';

const STATUS_CONFIG = {
  submitted: {
    label: 'Submitted',
    icon: Clock,
    color: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    description: 'Your appeal has been submitted and is awaiting review.',
  },
  under_review: {
    label: 'Under Review',
    icon: Eye,
    color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20',
    description: 'A moderator is currently reviewing your appeal.',
  },
  approved: {
    label: 'Approved',
    icon: CheckCircle,
    color: 'bg-green-500/10 text-green-500 border-green-500/20',
    description: 'Your appeal has been approved.',
  },
  rejected: {
    label: 'Rejected',
    icon: XCircle,
    color: 'bg-red-500/10 text-red-500 border-red-500/20',
    description: 'Your appeal has been rejected.',
  },
  withdrawn: {
    label: 'Withdrawn',
    icon: MinusCircle,
    color: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
    description: 'You withdrew this appeal.',
  },
};

function formatDate(dateString) {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(dateString) {
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function AppealCard({ appeal, onViewDetails, onWithdraw }) {
  const status = STATUS_CONFIG[appeal.status];
  const StatusIcon = status?.icon || AlertCircle;

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              {appeal.appealId}
              <Badge variant="outline" className={cn('ml-2', status?.color)}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {status?.label}
              </Badge>
            </CardTitle>
            <CardDescription className="mt-1">
              {appeal.moderationActionType === 'user_suspended'
                ? 'Account Suspension'
                : 'Account Ban'}{' '}
              Appeal
            </CardDescription>
          </div>
          <span className="text-xs text-muted-foreground">
            {formatDate(appeal.createdAt)}
          </span>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Original action info */}
        <div className="rounded-lg border bg-muted/30 p-3 text-sm">
          <p className="font-medium">Original Action Reason:</p>
          <p className="text-muted-foreground mt-1 line-clamp-2">
            {appeal.originalActionSnapshot?.reason}
          </p>
        </div>

        {/* Appeal reason preview */}
        <div>
          <p className="text-sm font-medium">Your Appeal:</p>
          <p className="text-sm text-muted-foreground mt-1 line-clamp-3">
            {appeal.reason}
          </p>
        </div>

        {/* Rejection reason if rejected */}
        {appeal.status === 'rejected' && appeal.rejectionReason && (
          <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-sm">
            <p className="font-medium text-red-600 dark:text-red-400">
              Rejection Reason:
            </p>
            <p className="text-red-700 dark:text-red-300 mt-1">
              {appeal.rejectionReason}
            </p>
          </div>
        )}

        {/* Reversal info if approved and reversed */}
        {appeal.status === 'approved' && appeal.reversalDetails?.wasReversed && (
          <div className="rounded-lg border border-green-500/20 bg-green-500/10 p-3 text-sm">
            <p className="font-medium text-green-600 dark:text-green-400">
              Original action has been reversed
            </p>
            {appeal.reversalDetails.reversalNotes && (
              <p className="text-green-700 dark:text-green-300 mt-1">
                {appeal.reversalDetails.reversalNotes}
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 border-t">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onViewDetails(appeal.appealId)}
          >
            <Eye className="h-4 w-4 mr-1" />
            View Details
          </Button>

          {appeal.status === 'submitted' && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => onWithdraw(appeal.appealId)}
            >
              Withdraw
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function AppealDetailsDialog({ appealId, open, onOpenChange }) {
  const { appeal, isLoading } = useAppeal(appealId);

  if (!open) return null;

  const status = appeal ? STATUS_CONFIG[appeal.status] : null;
  const StatusIcon = status?.icon || AlertCircle;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Appeal Details
            {status && (
              <Badge variant="outline" className={cn('ml-2', status.color)}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {status.label}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {appeal?.appealId} - Submitted {appeal && formatDateTime(appeal.createdAt)}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : appeal ? (
          <div className="space-y-6">
            {/* Status description */}
            <div className={cn('p-3 rounded-lg border', status?.color)}>
              <p className="text-sm">{status?.description}</p>
            </div>

            {/* Original Action */}
            <div>
              <h4 className="font-semibold mb-2">Original Moderation Action</h4>
              <div className="rounded-lg border bg-muted/30 p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Action:</span>
                  <span className="font-medium">
                    {appeal.originalActionSnapshot?.actionType === 'user_suspended'
                      ? 'Account Suspension'
                      : 'Account Ban'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date:</span>
                  <span>{formatDateTime(appeal.originalActionSnapshot?.createdAt)}</span>
                </div>
                {appeal.originalActionSnapshot?.duration && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Duration:</span>
                    <span>{appeal.originalActionSnapshot.duration} hours</span>
                  </div>
                )}
                <div className="pt-2 border-t">
                  <span className="text-muted-foreground">Reason:</span>
                  <p className="mt-1">{appeal.originalActionSnapshot?.reason}</p>
                </div>
              </div>
            </div>

            {/* Appeal Reason */}
            <div>
              <h4 className="font-semibold mb-2">Your Appeal</h4>
              <div className="rounded-lg border p-4">
                <p className="text-sm whitespace-pre-wrap">{appeal.reason}</p>
              </div>
            </div>

            {/* Additional Evidence */}
            {appeal.additionalEvidence && (
              <div>
                <h4 className="font-semibold mb-2">Additional Evidence</h4>
                <div className="rounded-lg border p-4">
                  <p className="text-sm whitespace-pre-wrap">
                    {appeal.additionalEvidence}
                  </p>
                </div>
              </div>
            )}

            {/* Review Info */}
            {appeal.reviewedAt && (
              <div>
                <h4 className="font-semibold mb-2">Review Details</h4>
                <div className="rounded-lg border p-4 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Reviewed by:</span>
                    <span>{appeal.reviewedByUsername || 'Moderator'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Reviewed at:</span>
                    <span>{formatDateTime(appeal.reviewedAt)}</span>
                  </div>
                  {appeal.reviewNotes && (
                    <div className="pt-2 border-t">
                      <span className="text-muted-foreground">Notes:</span>
                      <p className="mt-1">{appeal.reviewNotes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Rejection Reason */}
            {appeal.status === 'rejected' && appeal.rejectionReason && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
                <h4 className="font-semibold text-red-600 dark:text-red-400 mb-2">
                  Rejection Reason
                </h4>
                <p className="text-sm">{appeal.rejectionReason}</p>
              </div>
            )}

            {/* Reversal Info */}
            {appeal.reversalDetails?.wasReversed && (
              <div className="rounded-lg border border-green-500/20 bg-green-500/10 p-4">
                <h4 className="font-semibold text-green-600 dark:text-green-400 mb-2">
                  Action Reversed
                </h4>
                <p className="text-sm">
                  The original moderation action has been reversed.
                </p>
                {appeal.reversalDetails.reversalNotes && (
                  <p className="text-sm mt-2">{appeal.reversalDetails.reversalNotes}</p>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className="text-muted-foreground">Appeal not found.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function MyAppeals() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedAppealId, setSelectedAppealId] = useState(null);

  const { appeals, pagination, isLoading, refetch } = useMyAppeals({
    page,
    limit: 10,
    status: statusFilter === 'all' ? null : statusFilter,
  });

  const { mutate: withdrawAppeal, isPending: isWithdrawing } = useWithdrawAppeal();

  const handleWithdraw = (appealId) => {
    if (
      window.confirm('Are you sure you want to withdraw this appeal? This cannot be undone.')
    ) {
      withdrawAppeal(appealId);
    }
  };

  return (
    <div className="container max-w-4xl py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">My Appeals</h1>
          <p className="text-muted-foreground">
            View and manage your moderation action appeals
          </p>
        </div>
        <Link to="/notifications">
          <Button variant="outline">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="under_review">Under Review</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="withdrawn">Withdrawn</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Refresh
        </Button>
      </div>

      {/* Appeals List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : appeals.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-40 text-center">
            <AlertCircle className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-muted-foreground">No appeals found</p>
            <p className="text-sm text-muted-foreground">
              You haven't submitted any appeals yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {appeals.map((appeal) => (
            <AppealCard
              key={appeal.appealId}
              appeal={appeal}
              onViewDetails={setSelectedAppealId}
              onWithdraw={handleWithdraw}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {page} of {pagination.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.min(pagination.pages, p + 1))}
            disabled={page === pagination.pages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Appeal Details Dialog */}
      <AppealDetailsDialog
        appealId={selectedAppealId}
        open={!!selectedAppealId}
        onOpenChange={(open) => !open && setSelectedAppealId(null)}
      />
    </div>
  );
}
